/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {CareFieldDef, CareFieldVal, defaultDateTimes, DefaultFieldImportance, Frequency, timeUnits} from "@app/app-shared/care_config";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {Card} from "../widget/Card";
import {Spacing} from "@styles/styles";
import {COLORS} from "@res/colors";
import {LoadingBar} from "../widget/LoadingBar";
import {EventuallyStateStatus} from "@modules/GoalsConfigModule";
import {HLine} from "../widget/HLine";
import {Loader} from "../../../widgets/Loader";
import {ConfigMeasurementsItem} from "../user-config/components/ConfigMeasurementsItem";
import {UICareGoal, windowType} from "@app/app-shared/api";

type ConfigGeneralProps = {
    configs: UICareGoal[]
    onCareConfigUpdate: (key: string, val: CareFieldVal) => void
    status?: EventuallyStateStatus
    fieldDefs: CareFieldDef[]
    patientId: string
    showActions: boolean
}

export class ConfigGeneral
    extends BaseComponent<ConfigGeneralProps> {

    resolveConfigStatus = () => {
        switch (this.props.status) {
            case EventuallyStateStatus.Saving:
                return "loading";
            case EventuallyStateStatus.Synced:
                return "success";
            case EventuallyStateStatus.Error:
            default:
                return "error";
        }
    };

    configRow = (i: number, val: CareFieldVal, f: CareFieldDef, toggle: boolean) => <React.Fragment key={f.key}>
        {i !== 0 && <HLine style={{backgroundColor: COLORS.color_gray_medium}}/>}
        <ConfigMeasurementsItem<Frequency>
            value={val}
            definition={f}
            toggle={toggle}
            onEnableChange={(enabled: boolean) => {
                return this.props.onCareConfigUpdate(
                    f.key, {
                        ...val,
                        enabled,
                        ...enabled ? {frequency: f.default} : {ranges: undefined, ttl: undefined}
                    }
                );
            }}
            onFrequencyChange={(cf: CareFieldVal) => {
                return this.props.onCareConfigUpdate(f.key, cf);
            }}
            showActions={this.props.showActions}
        />
    </React.Fragment>;

    render() {
        const configs = this.props.configs;

        if (!configs)
            return <div className={"match_height"} style={{position: "relative"}}><Loader/></div>;

        return <Card style={{position: "relative", padding: `${Spacing.xs}px ${3 * Spacing.s}px`}}>
            {this.props.status && <LoadingBar status={this.resolveConfigStatus()}/>}
            {
                this.props.fieldDefs
                    .filter((f) => {
                        return f.show;
                    })
                    .map((f, i) => {

                        const config = configs.find(goal => goal.name === f.key)
                        let val: CareFieldVal
                        if (config) {
                            val = {
                                enabled: true,
                                frequency: {
                                    type: "unit",
                                    times: config.threshold,
                                    value: timeUnits[windowType.indexOf(config.windowType)]
                                },
                                dateTimes: defaultDateTimes(),
                                importance: DefaultFieldImportance,
                                ranges: config.ranges
                            }
                        } else {
                            val = {
                                enabled: false,
                                frequency: f.default,
                                dateTimes: defaultDateTimes(),
                                importance: DefaultFieldImportance
                            }
                        }

                        return this.configRow(i, val, f, f.toggle);
                    })
            }
        </Card>;
    }
}
